import React from "react";
import { Clock } from "lucide-react";
import { DASHBOARD_COLORS } from "../../constants/dashboard";
import { ShimmerBlock } from "../common/Skeleton";
import type { ITodayStatus } from "../../store/apis/attendance.api";

interface DashboardHeroProps {
  firstName?: string;
  loading?: boolean;
  todayStatus?: ITodayStatus;
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function formatTodayDate(): string {
  const today = new Date();
  return today.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

const DashboardHero: React.FC<DashboardHeroProps> = ({
  firstName,
  loading = false,
  todayStatus,
}) => {
  const hasCheckIn = Boolean(todayStatus?.checkInTime);
  const hasCheckOut = Boolean(todayStatus?.checkOutTime);
  const pillState =
    hasCheckIn && !hasCheckOut
      ? "checked-in"
      : hasCheckIn && hasCheckOut
        ? "checked-out"
        : "not-started";

  const pillLabel =
    pillState === "checked-in"
      ? "Checked in at"
      : pillState === "checked-out"
        ? "Checked out at"
        : "Check-in";

  const pillValue =
    pillState === "checked-in"
      ? formatTime(todayStatus?.checkInTime)
      : pillState === "checked-out"
        ? formatTime(todayStatus?.checkOutTime)
        : "—";

  const statusLine =
    pillState === "checked-in"
      ? "You're checked in. Have a productive day!"
      : pillState === "checked-out"
        ? "You've checked out for today."
        : "Not checked in yet today.";

  if (loading) {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-layout-subtle p-6 sm:p-8">
        <div className="flex items-center justify-between gap-5 flex-wrap">
          <div className="space-y-3">
            <ShimmerBlock className="h-7 w-56 rounded-md" />
            <ShimmerBlock className="h-4 w-72 rounded-md" />
          </div>
          <ShimmerBlock className="h-16 w-52 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden rounded-[20px] p-6 sm:p-8 text-white shadow-[0_18px_40px_-18px_rgba(109,40,217,0.6)]"
      style={{ background: DASHBOARD_COLORS.brandGrad }}
    >
      <div className="absolute -top-10 -right-8 w-[200px] h-[200px] rounded-full bg-white/10" />
      <div className="absolute -bottom-16 right-24 w-[150px] h-[150px] rounded-full bg-white/[0.07]" />

      <div className="relative flex items-center justify-between gap-5 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-[26px] font-extrabold tracking-tight text-white">
            {getGreeting()}, {firstName || "there"} 👋
          </h1>
          <p className="mt-2 text-[13.5px] text-white/80">
            {formatTodayDate()} · {statusLine}
          </p>
        </div>

        <div className="flex items-center gap-3.5 bg-white/[0.14] border border-white/20 rounded-[15px] px-4 py-3.5 backdrop-blur flex-none">
          <span className="w-11 h-11 rounded-[13px] bg-white/20 grid place-items-center flex-none">
            <Clock className="w-[23px] h-[23px] text-white" />
          </span>
          <div>
            <div className="text-[11.5px] font-semibold text-white/80">
              {pillLabel}
            </div>
            <div className="text-[21px] font-extrabold text-white tracking-tight leading-tight">
              {pillValue}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHero;
